// Pure, framework-free logic for public tournament sign-ups: phone-number
// normalization, field validation and the registration builder. Kept free of
// React/persistence imports so it can be shared by the public form, the API
// server and unit tests alike.

import type { Registration, RegistrationInput, TournamentState } from './types';
import {
  DEFAULT_DIAL_CODE,
  REGISTRATION_MIN_BIRTH_YEAR,
  REGISTRATION_NAME_MAX,
} from './types';
import { registrationEntityId } from './ids';

/**
 * Normalize a Cambodian mobile number to E.164 (`+855…`), or return null when it
 * cannot be a valid Cambodian mobile. Accepts the common local format (a leading
 * `0`, e.g. `012 345 678`), an international `+855`/`855` prefix, or a bare
 * national number, ignoring spaces, dashes, dots and parentheses. The national
 * part must be 8–9 digits (Cambodian mobiles are 8 or 9 digits after the 0).
 */
export function normalizeCambodianMobile(raw: string): string | null {
  const compact = raw.replace(/[\s\-().]/g, '');
  if (compact.length === 0) return null;
  // Only an optional leading "+" followed by digits is acceptable.
  if (!/^\+?\d+$/.test(compact)) return null;

  let national: string;
  if (compact.startsWith('+855')) national = compact.slice(4);
  else if (compact.startsWith('855')) national = compact.slice(3);
  else if (compact.startsWith('0')) national = compact.slice(1);
  else national = compact.replace(/^\+/, '');

  if (!/^\d{8,9}$/.test(national)) return null;
  return `${DEFAULT_DIAL_CODE}${national}`;
}

/** Per-field validation messages; an absent key means that field is valid. */
export interface RegistrationErrors {
  fullName?: string;
  dateOfBirth?: string;
  mobile?: string;
}

export interface RegistrationValidation {
  valid: boolean;
  errors: RegistrationErrors;
  /** Normalized, trimmed fields — present only when `valid` is true. */
  value: RegistrationInput | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether `value` is a real calendar date in `YYYY-MM-DD` form (no overflow). */
export function isRealIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Validate and normalize the raw fields from the public registration form.
 * `now` (epoch ms) is injected so date-of-birth bounds are testable; it defaults
 * to the current time. Returns trimmed name, the ISO date and an E.164 mobile.
 */
export function validateRegistration(
  input: RegistrationInput,
  now: number = Date.now(),
): RegistrationValidation {
  const errors: RegistrationErrors = {};

  const fullName = input.fullName.trim();
  if (fullName.length === 0) {
    errors.fullName = 'Full name is required.';
  } else if (fullName.length > REGISTRATION_NAME_MAX) {
    errors.fullName = `Full name must be ${REGISTRATION_NAME_MAX} characters or fewer.`;
  }

  const dateOfBirth = input.dateOfBirth.trim();
  if (dateOfBirth.length === 0) {
    errors.dateOfBirth = 'Date of birth is required.';
  } else if (!isRealIsoDate(dateOfBirth)) {
    errors.dateOfBirth = 'Enter a valid date of birth.';
  } else {
    const year = Number(dateOfBirth.slice(0, 4));
    const dobMs = Date.parse(`${dateOfBirth}T00:00:00Z`);
    if (year < REGISTRATION_MIN_BIRTH_YEAR) {
      errors.dateOfBirth = 'Enter a valid date of birth.';
    } else if (dobMs > now) {
      errors.dateOfBirth = 'Date of birth cannot be in the future.';
    }
  }

  const mobile = normalizeCambodianMobile(input.mobile);
  if (input.mobile.trim().length === 0) {
    errors.mobile = 'Mobile number is required.';
  } else if (mobile === null) {
    errors.mobile = 'Enter a valid Cambodian mobile number.';
  }

  const valid = Object.keys(errors).length === 0;
  return {
    valid,
    errors,
    value: valid ? { fullName, dateOfBirth, mobile: mobile as string } : null,
  };
}

/**
 * Whether the tournament is currently accepting public sign-ups: the organiser
 * enabled them, the tournament has not started yet (roster still open) and the
 * sign-up deadline, if any, has not passed. `now` defaults to the current time.
 */
export function isRegistrationOpen(
  state: Pick<
    TournamentState,
    'acceptPublicRegistration' | 'registrationClosesAt' | 'status'
  >,
  now: number = Date.now(),
): boolean {
  if (!state.acceptPublicRegistration) return false;
  if (state.status !== 'DRAFT') return false;
  if (state.registrationClosesAt !== null && now >= state.registrationClosesAt) {
    return false;
  }
  return true;
}

/**
 * Build a fresh, UNVERIFIED `Registration` from already-validated, normalized
 * input. A sign-up starts UNVERIFIED — it only becomes a real, organiser-visible
 * PENDING request once the registrant confirms the OTP sent to their mobile (see
 * `src/domain/otp.ts`). `id` and `createdAt` are injectable for deterministic
 * tests; they default to a UUID-based id and the current time.
 */
export function createRegistration(
  input: RegistrationInput,
  options: { id?: string; createdAt?: number } = {},
): Registration {
  return {
    id: options.id ?? registrationEntityId(crypto.randomUUID()),
    fullName: input.fullName,
    dateOfBirth: input.dateOfBirth,
    mobile: input.mobile,
    status: 'UNVERIFIED',
    playerKey: null,
    createdAt: options.createdAt ?? Date.now(),
  };
}
